"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Maximize2, Printer, ZoomIn, ZoomOut } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { inlineHref } from "./inline-href";
import { fitPage, isIos, pageAtScroll } from "./pdf-ios";

type Loaded = { url: string; pages: number; bytes: ArrayBuffer | null };
type Shown = Loaded | { failed: true } | null;

/** Fetches the document at `href` and gives it an in-page address (a blob), so the browser shows it with its own PDF reader whatever the server's download headers say. `pages` is its page count (1 for a picture). */
async function loadBlob(href: string): Promise<Loaded> {
  const response = await fetch(inlineHref(href), { credentials: "same-origin" });
  if (!response.ok) throw new Error("document-unavailable");
  const isPdf = (response.headers.get("content-type") ?? "").includes("pdf");
  const bytes = await response.arrayBuffer();
  const pages = isPdf ? (await (await import("pdf-lib")).PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount() : 1;
  return { url: URL.createObjectURL(new Blob([bytes], isPdf ? { type: "application/pdf" } : { type: response.headers.get("content-type") ?? "" })), pages, bytes: isPdf ? bytes : null };
}

/** Safari (and every browser on iOS) ignores the page and zoom in the address, so the toolbar leaves those buttons out there; its own reader has controls. Chrome, Edge and Firefox mark themselves in the agent text. */
function readerFollowsAddress(): boolean {
  return !/^((?!chrome|chromium|android|crios|fxios|edg).)*(safari|iphone|ipad)/i.test(navigator.userAgent);
}

const ZOOM_STEPS = [50, 75, 100, 125, 150, 200, 300];

function Tool({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return <Button type="button" variant="ghost" size="icon-sm" aria-label={label} title={label} disabled={disabled} onClick={onClick}>{children}</Button>;
}

type View = { page: number; zoom: number | null };
type ToolbarProps = { ios: boolean; loaded: Loaded; frame: RefObject<HTMLIFrameElement | null>; view: View; onView: (view: View) => void };

/** The bar over the reader (the page and zoom live in the parent, which reopens the reader at them): previous and next page with "n / N", zoom out and in, fit the width, Print, and Open in a new tab. */
function Toolbar({ ios, loaded, frame, view: { page, zoom }, onView }: ToolbarProps) {
  const t = useTranslations("pdfPreview");
  const go = (nextPage: number, nextZoom: number | null) => onView({ page: nextPage, zoom: nextZoom });
  const step = (direction: 1 | -1) => ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, ZOOM_STEPS.findIndex((z) => z >= (zoom ?? 100)) + direction))];
  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-3 py-1.5">
      {(ios || readerFollowsAddress()) && <>
        <Tool label={t("previous")} disabled={page <= 1} onClick={() => go(page - 1, zoom)}><ChevronUp aria-hidden /></Tool>
        <Tool label={t("next")} disabled={page >= loaded.pages} onClick={() => go(page + 1, zoom)}><ChevronDown aria-hidden /></Tool>
        <span className="px-2 text-xs tabular-nums text-muted-foreground" dir="ltr">{page} / {loaded.pages}</span>
        <Tool label={t("zoomOut")} onClick={() => go(page, step(-1))}><ZoomOut aria-hidden /></Tool>
        <Tool label={t("zoomIn")} onClick={() => go(page, step(1))}><ZoomIn aria-hidden /></Tool>
        <Tool label={t("fitWidth")} onClick={() => go(page, null)}><Maximize2 aria-hidden /></Tool>
      </>}
      <span className="ms-auto flex items-center gap-1">
        <Tool label={t("print")} onClick={() => { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print(); }}><Printer aria-hidden /></Tool>
        <Button type="button" variant="ghost" size="icon-sm" nativeButton={false} aria-label={t("open")} title={t("open")} render={<a href={loaded.url} target="_blank" rel="noopener" />}><ExternalLink aria-hidden /></Button>
      </span>
    </div>
  );
}

/** Draws every page into its canvas, one after the other, stopping when `alive()` turns false. */
async function drawPages(pdf: import("pdfjs-dist").PDFDocumentProxy, canvases: (HTMLCanvasElement | null)[], width: number, zoom: number, tasks: { cancel: () => void }[], alive: () => boolean) {
  for (let n = 1; n <= pdf.numPages; n += 1) {
    const page = await pdf.getPage(n);
    const canvas = canvases[n - 1];
    if (!alive() || !canvas) return;
    const base = page.getViewport({ scale: 1 });
    const fit = fitPage(base.width, base.height, width, zoom, window.devicePixelRatio || 1);
    const viewport = page.getViewport({ scale: fit.renderScale });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${fit.cssWidth}px`;
    canvas.style.height = `${fit.cssHeight}px`;
    const task = page.render({ canvas, viewport });
    tasks.push(task);
    await task.promise;
  }
}

/** iOS shows only page 1 of a PDF in an iframe, so the pages are drawn with pdfjs (loaded only here) as a scrolling stack of canvases. The page shown follows the scroll; `view.page` changes from the toolbar scroll to that page. `onFail` falls back to the open-in-new-tab button. */
function IosPages({ bytes, view, onView, onFail }: { bytes: ArrayBuffer; view: View; onView: (view: View) => void; onFail: () => void }) {
  const t = useTranslations("pdfPreview");
  const box = useRef<HTMLDivElement>(null);
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const seen = useRef(1);
  const [pdf, setPdf] = useState<import("pdfjs-dist").PDFDocumentProxy | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    let current = true;
    let task: import("pdfjs-dist").PDFDocumentLoadingTask | undefined;
    (async () => {
      const pdfjs = await import("pdfjs-dist");
      pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
      task = pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) });
      const doc = await task.promise;
      if (current) setPdf(doc);
    })().catch(() => { if (current) onFail(); });
    return () => { current = false; void task?.destroy(); };
  }, [bytes, onFail]);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, [pdf]);
  const zoom = view.zoom ?? 100;
  useEffect(() => {
    if (!pdf || !width) return;
    let current = true;
    const tasks: { cancel: () => void }[] = [];
    drawPages(pdf, canvases.current, width, zoom, tasks, () => current)
.catch((error: unknown) => { if (current && (error as { name?: string }).name !== "RenderingCancelledException") onFail(); });
    return () => { current = false; tasks.forEach((task) => task.cancel()); };
  }, [pdf, width, zoom, onFail]);
  useEffect(() => {
    if (view.page === seen.current) return;
    seen.current = view.page;
    canvases.current[view.page - 1]?.scrollIntoView({ block: "start" });
  }, [view.page]);
  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    const tops = canvases.current.map((c) => (c ? c.offsetTop - el.offsetTop : 0));
    const page = pageAtScroll(tops, el.scrollTop, el.clientHeight);
    if (page !== seen.current) { seen.current = page; onView({ page, zoom: view.zoom }); }
  };
  return (
    <div ref={box} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-auto bg-muted/20">
      {!pdf && <p className="p-6 text-center text-muted-foreground">{t("loading")}</p>}
      {pdf && Array.from({ length: pdf.numPages }, (_, i) => <canvas key={i} ref={(el) => { canvases.current[i] = el; }} className="mx-auto mb-2 block bg-white" />)}
    </div>
  );
}

/** The document at `href` in the browser's own PDF reader (Chrome, Edge, Firefox and Safari all have one) under a toolbar of ours: page and zoom go in the address fragment and the reader is reopened at them (changing a fragment alone is ignored by the readers), so it is reset to the toolbar's page when a button is used; Safari ignores the fragment. A browser with no reader (`navigator.pdfViewerEnabled` false, such as a phone) gets a button that opens the file instead. */
export function PdfViewer({ href }: { href: string }) {
  const t = useTranslations("pdfPreview");
  const frame = useRef<HTMLIFrameElement>(null);
  const [shown, setShown] = useState<Shown>(null);
  const [ios] = useState(() => isIos(navigator.userAgent, navigator.platform, navigator.maxTouchPoints));
  const [pagesFailed, setPagesFailed] = useState(false);
  const onFail = useCallback(() => setPagesFailed(true), []);
  const [view, setView] = useState<View>({ page: 1, zoom: null });
  useEffect(() => {
    let current = true;
    let made = "";
    loadBlob(href).then((loaded) => { made = loaded.url; if (current) setShown(loaded); }, () => { if (current) setShown({ failed: true }); });
    return () => { current = false; setShown(null); if (made) URL.revokeObjectURL(made); };
  }, [href]);
  const box = "flex h-[72vh] flex-col items-center justify-center gap-3 text-center";
  if (!shown) return <p className={`${box} text-muted-foreground`}>{t("loading")}</p>;
  if ("failed" in shown) return <p className={`${box} text-destructive`}>{t("failed")}</p>;
  const drawn = ios && !!shown.bytes && !pagesFailed;
  if (!drawn && (navigator.pdfViewerEnabled === false || (ios && shown.bytes))) return <div className={box}><p className="text-muted-foreground">{t("noViewer")}</p><Button nativeButton={false} render={<a href={shown.url} target="_blank" rel="noopener" />}>{t("open")}</Button></div>;
  return (
    <div className="relative flex h-[72vh] flex-col">
      <Toolbar ios={drawn} loaded={shown} frame={frame} view={view} onView={setView} />
      {drawn && shown.bytes
        ? <><IosPages bytes={shown.bytes} view={view} onView={setView} onFail={onFail} /><iframe ref={frame} src={shown.url} title={t("help")} aria-hidden tabIndex={-1} className="pointer-events-none absolute size-px opacity-0" /></>
        : <iframe key={`${view.page}-${view.zoom}`} ref={frame} src={`${shown.url}#page=${view.page}&${view.zoom ? `zoom=${view.zoom}` : "view=FitH"}`} title={t("help")} className="min-h-0 w-full flex-1 border-0 bg-muted/20" />}
    </div>
  );
}
